import { assignGroupToShift } from "@/api/Shifts";
import { useErrorMessageAlert } from "@/Utils/ErrorUtils";
import { useDragAndDropContext } from "./DragAndDropContext";
import * as Sentry from "@sentry/react";
import { useDragAndDropMutation } from "./dragAndDropUtils";
import { unassignGroupFromRoute } from "@/api/Routes";
import { useLingui } from "@lingui/react/macro";
import { isGroup } from "@medoma/models/activities";

export const useDropToRoute = (shiftId: number, routeId: string) => {
  const { t } = useLingui();
  const { displayErrorMessageAlert } = useErrorMessageAlert();
  const { drag } = useDragAndDropContext();

  const { mutate: placeGroupInRoute, isPending: isPlacingGroupInRoute } =
    useDragAndDropMutation({
      mutationFn: ({ groupId }: { groupId: string }) => {
        return assignGroupToShift(groupId, shiftId, routeId);
      },
      errorMessage: t`Det gick inte att lägga till besöket i rutten.`,
    });

  const { mutate: moveVisitToRoute, isPending: isMovingVisitToRoute } =
    useDragAndDropMutation({
      mutationFn: async ({
        visitId,
        sourceRouteId,
      }: {
        visitId: string;
        sourceRouteId: string;
      }) => {
        await unassignGroupFromRoute(visitId, sourceRouteId);
        return assignGroupToShift(visitId, shiftId, routeId);
      },
      errorMessage: t`Det gick inte att flytta besöket till rutten.`,
    });

  const handleDrop = () => {
    if (!drag) {
      Sentry.captureException(
        new Error("A dropped item is missing the mandatory 'drag'."),
      );
      displayErrorMessageAlert(
        t`Något gick fel. Prova igen. Om felet kvarstår, hör av dig till teknisk support.`,
      );
      return;
    }

    switch (drag.source) {
      case "sidebar":
        // A route takes groups. A lone occurrence in the sidebar is an
        // inconsistency, and is handled nowhere yet.
        return isGroup(drag.item)
          ? placeGroupInRoute({ groupId: drag.item.id })
          : undefined;
      case "route":
        return moveVisitToRoute({
          visitId: drag.item.id,
          sourceRouteId: drag.routeId,
        });
      case "shift-lane":
        // The shift lane holds what is not route work.
        return undefined;
    }
  };

  return {
    handleDrop,
    isPending: isPlacingGroupInRoute || isMovingVisitToRoute,
  };
};
